import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { GOAL_LABELS } from '../../utils/expertRef';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';

const Page = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 32px 20px 60px;
`;

const Box = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Btn = styled.button`
  background: #ff6b35;
  color: #fff;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 8px;
  font-size: 14px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled(Box)`
  text-align: center;
  padding: 48px 24px;

  h2 {
    margin: 0 0 12px;
    font-size: 1.5rem;
    color: #333;
  }

  p {
    color: #666;
    line-height: 1.6;
    max-width: 520px;
    margin: 0 auto 24px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionLink = styled(Link)`
  display: inline-block;
  padding: 12px 20px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  background: #ff6b35;
  color: #fff;

  &.secondary {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Notice = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  background: ${(p) => (p.$variant === 'warning' ? '#fff8e1' : '#e8f5e9')};
  color: ${(p) => (p.$variant === 'warning' ? '#8a6d00' : '#2e7d32')};
`;

const ExpertCabinet = () => {
  const [loading, setLoading] = useState(true);
  const [expert, setExpert] = useState(null);
  const [collections, setCollections] = useState([]);
  const [balance, setBalance] = useState(null);
  const [articles, setArticles] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', goal: 'wellness', productIds: '' });
  const [newsForm, setNewsForm] = useState({ title: '', slug: '', content: '' });

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const load = async () => {
    setLoading(true);
    try {
      const meRes = await fetch(`${API_BASE_URL}/me/expert`, { headers: headers() });
      const me = await meRes.json();
      if (!me.data) {
        setExpert(null);
        return;
      }
      setExpert(me.data);

      if (!me.data.is_active) {
        return;
      }

      const [col, bal, news, prog] = await Promise.all([
        fetch(`${API_BASE_URL}/me/expert/collections`, { headers: headers() }).then((r) => r.json()),
        fetch(`${API_BASE_URL}/me/expert/digital-balance`, { headers: headers() }).then((r) => r.json()),
        fetch(`${API_BASE_URL}/me/expert/news`, { headers: headers() }).then((r) => r.json()),
        fetch(`${API_BASE_URL}/me/expert/my-programs`, { headers: headers() }).then((r) => r.json())
      ]);
      setCollections(col.data || []);
      setBalance(bal.data);
      setArticles(news.data || []);
      setPrograms(prog.data || []);
    } catch {
      setExpert(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addCollection = async () => {
    const productIds = form.productIds.split(',').map((s) => parseInt(s.trim(), 10)).filter(Boolean);
    await fetch(`${API_BASE_URL}/me/expert/collections`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ title: form.title, description: form.description, goal: form.goal, productIds })
    });
    setForm({ title: '', description: '', goal: 'wellness', productIds: '' });
    load();
  };

  const submitNews = async () => {
    await fetch(`${API_BASE_URL}/me/expert/news`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ ...newsForm, status: 'pending_moderation' })
    });
    setNewsForm({ title: '', slug: '', content: '' });
    load();
  };

  const layout = (content) => (
    <>
      <DashboardHeader />
      <Page>{content}</Page>
      <DashboardFooter />
    </>
  );

  if (loading) {
    return layout(<p style={{ color: '#666' }}>Загрузка кабинета эксперта...</p>);
  }

  if (!expert) {
    return layout(
      <EmptyState>
        <h2>Кабинет эксперта</h2>
        <p>
          У вашего аккаунта пока нет статуса эксперта. Подайте заявку — после одобрения администратором
          здесь появятся подборки товаров, статьи и статистика digital-вознаграждений.
        </p>
        <Actions>
          <ActionLink to="/become-expert">Подать заявку эксперта</ActionLink>
          <ActionLink to="/experts" className="secondary">Смотреть экспертов</ActionLink>
          <ActionLink to="/dashboard/my-programs" className="secondary">Мои программы</ActionLink>
        </Actions>
      </EmptyState>
    );
  }

  return layout(
    <>
      <h1 style={{ marginBottom: 8 }}>Кабинет эксперта</h1>
      {expert.slug ? (
        <p style={{ color: '#666', marginBottom: 24 }}>
          Публичная страница:{' '}
          <Link to={`/expert/${expert.slug}`} target="_blank" rel="noreferrer">
            /expert/{expert.slug}
          </Link>
          {!expert.is_published && (
            <span style={{ color: '#b45309', marginLeft: 8 }}>— ещё не опубликована</span>
          )}
        </p>
      ) : (
        <Notice $variant="warning">
          Публичная страница не настроена. Обратитесь к администратору для публикации профиля эксперта.
        </Notice>
      )}

      {!expert.is_active && (
        <Notice $variant="warning">
          Аккаунт эксперта ожидает активации администратором. Редактирование подборок и статей будет доступно после активации.
        </Notice>
      )}

      {expert.is_active && (
        <>
          <Box>
            <h3 style={{ marginTop: 0 }}>Вознаграждение за digital</h3>
            <p style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>
              {balance?.balance?.toLocaleString?.('ru-RU') ?? balance?.balance ?? 0} ₽
            </p>
          </Box>

          <Box>
            <h3 style={{ marginTop: 0 }}>Мои подборки ({collections.length}/4)</h3>
            {collections.map((c) => (
              <div key={c.id} style={{ marginBottom: 12, padding: 12, background: '#f9f9f9', borderRadius: 8 }}>
                <strong>{c.title}</strong> — {GOAL_LABELS[c.goal] || c.goal}
                <div style={{ fontSize: 13, color: '#666' }}>Товары: {(c.product_ids || []).join(', ') || '—'}</div>
              </div>
            ))}
            {collections.length < 4 && (
              <div>
                <input
                  placeholder="Название"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  style={{ width: '100%', marginBottom: 8, padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                />
                <textarea
                  placeholder="Описание"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  style={{ width: '100%', marginBottom: 8, padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                />
                <select
                  value={form.goal}
                  onChange={(e) => setForm({ ...form, goal: e.target.value })}
                  style={{ marginBottom: 8, padding: 8 }}
                >
                  {Object.entries(GOAL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
                <input
                  placeholder="ID товаров через запятую"
                  value={form.productIds}
                  onChange={(e) => setForm({ ...form, productIds: e.target.value })}
                  style={{ width: '100%', marginBottom: 8, padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                />
                <Btn type="button" onClick={addCollection}>Добавить подборку</Btn>
              </div>
            )}
          </Box>

          <Box>
            <h3 style={{ marginTop: 0 }}>Мои статьи</h3>
            {articles.length === 0 && <p style={{ color: '#666' }}>Статей пока нет</p>}
            {articles.map((a) => (
              <div key={a.id} style={{ marginBottom: 8 }}>
                {a.title} — <em>{a.status}</em>
              </div>
            ))}
            <h4>Новая статья (на модерацию)</h4>
            <input
              placeholder="Заголовок"
              value={newsForm.title}
              onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
              style={{ width: '100%', marginBottom: 8, padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
            />
            <input
              placeholder="slug-latиницей"
              value={newsForm.slug}
              onChange={(e) => setNewsForm({ ...newsForm, slug: e.target.value })}
              style={{ width: '100%', marginBottom: 8, padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
            />
            <textarea
              placeholder="Текст HTML"
              value={newsForm.content}
              onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
              style={{ width: '100%', minHeight: 120, marginBottom: 8, padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
            />
            <Btn type="button" onClick={submitNews}>Отправить на модерацию</Btn>
          </Box>
        </>
      )}

      <Box>
        <h3 style={{ marginTop: 0 }}>Купленные программы</h3>
        {programs.length === 0 ? (
          <p style={{ color: '#666', margin: 0 }}>
            Пока нет купленных программ. <Link to="/experts">Смотреть экспертов</Link>
          </p>
        ) : (
          programs.map((p) => (
            <div key={p.id || p.digital_product_id} style={{ marginBottom: 8 }}>
              {p.title} — {p.expert_name}
            </div>
          ))
        )}
        <p style={{ marginTop: 16, marginBottom: 0 }}>
          <Link to="/dashboard/my-programs">Все мои программы →</Link>
        </p>
      </Box>
    </>
  );
};

export default ExpertCabinet;
